import React, { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * Satellite + labels map picker for the host "Add an Experience" flow.
 *
 * - Uses ESRI World Imagery for satellite tiles, layered with ESRI's
 *   Reference/World_Boundaries_and_Places for a hybrid look. No API key
 *   required; free with attribution.
 * - Centers on the host's listing coordinates so we don't need location
 *   permission from the host.
 * - Long-press or drag to move the pin.
 * - Tap anywhere on the map to drop the pin there.
 */
const HYBRID_STYLE = {
  version: 8,
  sources: {
    'esri-satellite': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: 'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      maxzoom: 19,
    },
    'esri-labels': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 19,
    },
  },
  layers: [
    { id: 'satellite', type: 'raster', source: 'esri-satellite' },
    { id: 'labels', type: 'raster', source: 'esri-labels', paint: { 'raster-opacity': 0.9 } },
  ],
};

export function ExperiencePinMap({
  latitude,
  longitude,
  onChange,
  height = 300,
  testId = 'host-experience-pin-map',
  pinColor = '#7C3AED',
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const lastSet = useRef({ lat: null, lng: null });

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: HYBRID_STYLE,
      center: [
        typeof longitude === 'number' ? longitude : 23.7275,
        typeof latitude === 'number' ? latitude : 37.9838,
      ],
      zoom: typeof latitude === 'number' && typeof longitude === 'number' ? 15 : 6,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      cooperativeGestures: false,
    });
    mapRef.current = map;

    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    const markerEl = document.createElement('div');
    markerEl.setAttribute('data-testid', `${testId}-pin`);
    markerEl.style.cssText = `
      width: 42px; height: 56px; display: flex; flex-direction: column; align-items: center;
      filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.55));
      cursor: grab;
    `;
    markerEl.innerHTML = `
      <div style="width:42px;height:42px;border-radius:50%;border:3px solid #FFFFFF;background:${pinColor};display:flex;align-items:center;justify-content:center;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1116 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
      <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:10px solid ${pinColor};margin-top:-4px;"></div>
    `;

    const marker = new maplibregl.Marker({ element: markerEl, anchor: 'bottom', draggable: true })
      .setLngLat([
        typeof longitude === 'number' ? longitude : 23.7275,
        typeof latitude === 'number' ? latitude : 37.9838,
      ])
      .addTo(map);
    markerRef.current = marker;

    marker.on('dragstart', () => { markerEl.style.cursor = 'grabbing'; });
    marker.on('dragend', () => {
      markerEl.style.cursor = 'grab';
      const lngLat = marker.getLngLat();
      lastSet.current = { lat: lngLat.lat, lng: lngLat.lng };
      onChange?.(lngLat.lat, lngLat.lng);
    });

    // Tap / click anywhere on the map moves the pin to that point.
    map.on('click', (event) => {
      marker.setLngLat(event.lngLat);
      lastSet.current = { lat: event.lngLat.lat, lng: event.lngLat.lng };
      onChange?.(event.lngLat.lat, event.lngLat.lng);
    });

    const handleResize = () => map.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      marker.remove();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    if (typeof latitude !== 'number' || typeof longitude !== 'number') return;
    if (lastSet.current.lat === latitude && lastSet.current.lng === longitude) return;
    markerRef.current.setLngLat([longitude, latitude]);
    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom: 15.6,
      duration: 800,
      essential: true,
    });
    lastSet.current = { lat: latitude, lng: longitude };
  }, [latitude, longitude]);

  return (
    <div
      data-testid={testId}
      style={{
        width: '100%',
        height,
        borderRadius: 20,
        overflow: 'hidden',
        position: 'relative',
        border: '1.5px solid var(--card-border)',
        boxShadow: '0 12px 28px rgba(20, 16, 50, 0.18)',
        background: '#0F1B2A',
      }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div
        style={{
          position: 'absolute',
          left: 12,
          bottom: 12,
          padding: '6px 10px',
          borderRadius: 999,
          background: 'rgba(20, 16, 50, 0.82)',
          color: '#FFFFFF',
          fontFamily: "'Inria Serif', serif",
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: '0.02em',
          backdropFilter: 'blur(6px)',
          pointerEvents: 'none',
        }}
      >
        Drag the pin or tap the map to place it
      </div>
    </div>
  );
}

export default ExperiencePinMap;
